import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { Pressable } from "react-native";
import { useFragment } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { Avatar } from "@whoosh/design-system/src/components/display";
import { GetCourseLayoutIconFromCode } from "@whoosh/design-system/src/components/icons_v2/courseLayout";
import {
  SubArrowIcon,
  RefreshIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  PunchPassAppliedIcon,
  PunchPassAvailableIcon,
} from "@whoosh/design-system/src/components/icons_v2/pos";
import { GetTransportIconFromCode } from "@whoosh/design-system/src/components/icons_v2/transportation";
import {
  Text,
  Box,
  HStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { bookingSummaryAnalyticsDataAtom } from "../../../../atoms/analytics/bookingSummaryAnalyticsDataAtom";
import { getBookingPlayerNoun } from "../../../../utils/getBookingPlayerNoun";
import { getRoundType } from "../../utils/getRoundType";
import { RoundInfoDisplay_Round$key } from "./__generated__/RoundInfoDisplay_Round.graphql";

type Props = {
  roundRef: RoundInfoDisplay_Round$key | null;
  totalPriceForRound: number | null;
  hideRoundPrice?: boolean;
  setRoundIdToReplace: (roundId: string) => void;
  setIsEditBookingModalOpen: (isEditBookingModalOpen: boolean) => void;
  canEditBooking?: boolean;
  onClick?: () => void;
  hasRatePunchPassApplied: boolean;
  hasTransportationPunchPassApplied: boolean;
  hasAvailableRatePunchPasses: boolean;
  hasAvailableTransportationPunchPasses: boolean;
};

export const RoundInfoDisplay = ({
  roundRef,
  totalPriceForRound,
  hideRoundPrice = false,
  setRoundIdToReplace,
  setIsEditBookingModalOpen,
  canEditBooking = true,
  hasRatePunchPassApplied,
  hasTransportationPunchPassApplied,
  hasAvailableRatePunchPasses,
  hasAvailableTransportationPunchPasses,
  onClick,
}: Props) => {
  const { currencySymbol } = useGetClubCurrency();

  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();

  const roundData = useFragment(RoundInfoDisplayFragmentString, roundRef);
  const playerNoun = getBookingPlayerNoun({
    bookingType: roundData?.booking?.__typename,
    copyOverrides:
      roundData?.booking?.startSlot?.agenda?.facility?.copyOverrides,
  });

  const isNonPlaying = !!roundData?.nonPlayingHost;

  /**
   * Get relevant Transportation to display
   */
  const transportationTypeCode = roundData?.transportationType?.code;
  const transportationTypeCustomCode =
    roundData?.transportationType?.customCode;
  const TransportationIcon =
    transportationTypeCode && GetTransportIconFromCode(transportationTypeCode);
  /**
   * Get relevant Course Layout to display
   */
  const courseLayoutHoleCount = roundData?.courseLayout?.holeCount;
  const CourseLayoutIcon = GetCourseLayoutIconFromCode(
    courseLayoutHoleCount ?? ""
  );

  const roundType = getRoundType(
    roundData?.__typename ?? "",
    !!roundData?.player?.id
  );

  const isUnknownGuestOrMemberHold =
    roundType === "UNKNOWN_GUEST" || roundType === "MEMBER_HOLD";

  const shouldShowReplacePlayer =
    canEditBooking &&
    isUnknownGuestOrMemberHold &&
    roundData?.booking?.canUpdate;

  const avatarType =
    roundType === "UNKNOWN_CUSTOMER"
      ? "unknownCustomer"
      : roundType === "CUSTOMER"
      ? "customer"
      : roundType === "GUEST" || roundType === "UNKNOWN_GUEST"
      ? "guest"
      : roundType === "MEMBER_HOLD"
      ? "memberHold"
      : "member";

  const ownerClientId = roundData?.booking?.ownerClient?.id ?? null;
  const isOwner =
    !!ownerClientId &&
    !!roundData?.player?.id &&
    roundData.player.id === ownerClientId;

  return (
    <Pressable
      onPress={() => {
        if (onClick) {
          onClick();
        }
      }}
      testID="round-info-display-pressable"
      accessibilityRole="button"
      accessibilityLabel={
        roundData?.player?.fullName
          ? t`Round for ${roundData.player.fullName}`
          : t`Round details`
      }
    >
      <HStack justifyContent="space-between" alignItems="center">
        <HStack gap={3} alignItems="center">
          {roundType === "GUEST" || roundType === "UNKNOWN_GUEST" ? (
            <SubArrowIcon />
          ) : null}
          <Avatar
            size="xs"
            profileImagePublicId={roundData?.player?.profileImage?.publicId}
            firstName={roundData?.player?.firstName}
            lastName={roundData?.player?.lastName}
            type={avatarType}
          />
          <Text variant="mainBaseSemiBold">
            {roundData?.player?.fullName ||
              (roundType === "MEMBER_HOLD" && t`Member Hold`) ||
              (roundType === "UNKNOWN_GUEST" && t`New Guest`) ||
              (roundType === "UNKNOWN_CUSTOMER" &&
                `${t`New`} ${playerNoun.singular}`)}
          </Text>
          <HStack gap={1} alignItems="center">
            {CourseLayoutIcon ? (
              <CourseLayoutIcon />
            ) : (
              <Text>{courseLayoutHoleCount}</Text>
            )}
            {roundData?.transportationType ? (
              <Box testID="round-info-transportation-type">
                {TransportationIcon ? (
                  <TransportationIcon />
                ) : (
                  <Text>{transportationTypeCustomCode}</Text>
                )}
              </Box>
            ) : null}
            <HStack
              alignItems="center"
              gap={3}
              marginLeft={!isNonPlaying ? 1 : 0}
            >
              {isNonPlaying ? (
                <Text
                  variant="mainBaseMedium"
                  color="text-secondary"
                >{t`(Non-Playing Host)`}</Text>
              ) : null}
              {isOwner ? (
                <Box
                  padding={1}
                  borderRadius="2"
                  borderColor="outline-default"
                  borderWidth={1}
                >
                  <Text variant="mainMicro">{t`OWNER`}</Text>
                </Box>
              ) : null}
            </HStack>
          </HStack>
        </HStack>
        <HStack alignItems="center" gap={2}>
          {hasRatePunchPassApplied ? (
            <PunchPassAppliedIcon />
          ) : hasAvailableRatePunchPasses ? (
            <PunchPassAvailableIcon />
          ) : null}

          {hasTransportationPunchPassApplied ? (
            <PunchPassAppliedIcon />
          ) : hasAvailableTransportationPunchPasses ? (
            <PunchPassAvailableIcon />
          ) : null}

          <Text variant="mainBaseSemiBold" color="text-primary">
            {!hideRoundPrice && totalPriceForRound != null
              ? `${currencySymbol}${ConvertCentsToDollars(totalPriceForRound)}`
              : ""}
          </Text>
        </HStack>

        {/* This is a temporary feature flag to show the replace player button */}
        {shouldShowReplacePlayer ? (
          <Box
            backgroundColor="surface-secondary"
            height={32}
            width={32}
            justifyContent="center"
            alignItems="center"
            borderRadius="2"
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t`Replace player`}
              onPress={() => {
                setIsEditBookingModalOpen(true);
                setRoundIdToReplace(roundData?.id ?? "");
                trackEvent(
                  "BookingDetail.ReplacePlayerClicked",
                  analyticsData ?? {}
                );
              }}
              testID="replace-player-button"
            >
              <RefreshIcon />
            </Pressable>
          </Box>
        ) : null}
      </HStack>
    </Pressable>
  );
};

const RoundInfoDisplayFragmentString = graphql`
  fragment RoundInfoDisplay_Round on Round {
    booking {
      __typename
      canUpdate
      startSlot {
        agenda {
          facility {
            type
            copyOverrides {
              lessonPlayer {
                singular
              }
              bookingPlayer {
                singular
              }
            }
          }
        }
      }
      ownerClient {
        ... on Customer {
          id
        }
      }
    }
    __typename
    id

    transportationType {
      id
      code
      customCode
      name
    }
    courseLayout {
      holeCount
    }
    ... on MemberRound {
      nonPlayingHost
    }
    player {
      id
      fullName
      firstName
      lastName
      profileImage {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
    }
  }
`;
